'use client';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, Check, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

interface FormSheetLayoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  heroTitle: string; 
  heroAction?: React.ReactNode;
  primaryAction: {
    label: string;
    onClick: () => void;
    isLoading?: boolean;
    disabled?: boolean;
  };
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  showBack?: boolean;
}

export function FormSheetLayout({
  isOpen,
  onOpenChange,
  heroTitle,
  heroAction,
  primaryAction,
  children,
  footerContent,
  showBack = false,
}: FormSheetLayoutProps) {
  
  // Lógica de botón atrás y sincronización de historial
  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ modalOpen: true }, '');
      
      const handlePopState = () => {
        // Si el usuario le da atrás físicamente
        onOpenChange(false);
      };

      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isOpen, onOpenChange]);

  const handleClose = () => {
    onOpenChange(false);
    // Si cerramos manualmente, y hay un estado en el historial, vamos atrás
    // para "limpiar" la URL pero asegurándonos de que el modal se cierre ya.
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      } else {
        onOpenChange(true);
      }
    }}>
      <SheetContent 
        side="bottom" 
        // Eliminamos dvh y usamos h-full con top-0 para bloquearlo arriba
        className="h-full w-full sm:max-w-md mx-auto overflow-hidden border-none p-0 shadow-2xl bg-white flex flex-col outline-none top-0 translate-y-0"
      >
        {/* HEADER NEGRO - STICKY Y Z-INDEX MÁXIMO */}
        <header className="sticky top-0 shrink-0 bg-zinc-950 px-6 h-16 flex items-center justify-between z-100 w-full border-b border-white/5">
          <button 
            onClick={handleClose}
            className="flex items-center gap-1.5 h-10 px-3 rounded-xl text-zinc-400 font-black text-[11px] uppercase tracking-widest active:bg-white/10 transition-colors"
          >
            {showBack ? (
              <>
                <ChevronLeft className="h-5 w-5" strokeWidth={3} />
                <span>Volver</span>
              </>
            ) : (
              "Cancelar"
            )}
          </button>

          <button
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled || primaryAction.isLoading}
            className={cn(
              "h-10 px-6 rounded-full font-black text-[11px] uppercase tracking-[0.15em] transition-all active:scale-95",
              "bg-white text-zinc-900 disabled:opacity-20 shadow-lg"
            )}
          >
            {primaryAction.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Listo"
            )}
          </button>
        </header>

        {/* ÁREA DE CONTENIDO - Es la única que puede scrollear */}
        <div className="flex-1 overflow-y-auto bg-white flex flex-col overscroll-none">
          
          {/* Hero Section - Sin gap superior */}
          <div className="p-8 pt-4 pb-4 flex items-center justify-between">
            <SheetHeader className="sr-only">
              <SheetTitle>{heroTitle}</SheetTitle>
              <SheetDescription>Formulario para {heroTitle}</SheetDescription>
            </SheetHeader>
            <h2 className="text-4xl font-black italic uppercase tracking-tighter text-zinc-900 leading-[0.9]">
              {heroTitle}
            </h2>
            {heroAction}
          </div>

          {/* Cuerpo del Formulario */}
          <div className="px-8 flex-1">
            {children}
          </div>

          {/* Botón Gigante Final */}
          <div className="p-8 pt-4 pb-12 mt-auto">
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.isLoading}
              className="h-20 w-full rounded-2xl bg-zinc-950 text-white font-black italic tracking-[0.2em] uppercase shadow-2xl active:scale-95 transition-all"
            >
              {primaryAction.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Check className="mr-3 h-6 w-6" strokeWidth={4} />
                  {primaryAction.label}
                </>
              )}
            </Button>
            
            {footerContent && (
              <div className="mt-6">
                {footerContent}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
